// src/store/thunks/organization/fetchOrganizationDashboard.ts
import { createAdminAsyncThunk } from '@/store/thunks/createAdminAsyncThunk';
import type { OrganizationSliceData } from '@/types/organization.types';
import {
  mapOrgProfileDTOToProfile,
  mapAdminDTOsToAdmins,
  mapOrgUserDTOsToRows,
} from '@/store/mappers/organization';

const MIN_LEN = 0;

export const fetchOrganizationDashboard = createAdminAsyncThunk<
  OrganizationSliceData,
  { id: string },
  { rejectValue: string }
>(
  'organization/fetchDashboard',
  async({ id }, { extra: { organizationService }, rejectWithValue }) => {
    try {
      // Previously: profile + admins + seats + users
      // Now: profile includes seats; do not call getSeatSummary() during bootstrap.
      const [profileDTO, adminsDTO, usersDTO] = await Promise.all([
        organizationService.getProfileById(id),
        organizationService.getAdmins(),
        organizationService.getUsers(),
      ]);

      // profile → profile object + optional seats
      const { profile, seats } = mapOrgProfileDTOToProfile(profileDTO);

      const administrators = mapAdminDTOsToAdmins(adminsDTO);
      const organizationUsers = mapOrgUserDTOsToRows(usersDTO);

      // if seats included in profile, use them; otherwise, fallback to seat service (rare)
      const internalSeats = seats?.internalSeats ?? { available: MIN_LEN, active: MIN_LEN, new: MIN_LEN, inactive: MIN_LEN };
      const externalSeats = seats?.externalSeats ?? { available: MIN_LEN, active: MIN_LEN, new: MIN_LEN, inactive: MIN_LEN };

      return {
        ...profile,
        administrators,
        internalSeats,
        externalSeats,
        organizationUsers,
      } satisfies OrganizationSliceData;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load organization dashboard';
      return rejectWithValue(msg);
    }
  }
);
